import { formatAud } from './pricing'
import type { HardwareKind, HardwareOrder, HardwareOrderStatus, HardwareProduct, HardwareUnit, HardwareUnitStatus } from '../api/hardware'

export const HARDWARE_KINDS: readonly HardwareKind[] = ['BEACON', 'GATEWAY', 'SENSOR', 'REPEATER']
export const KIND_LABEL: Record<HardwareKind, string> = { GATEWAY: 'Gateway', BEACON: 'Beacon', SENSOR: 'Sensor', REPEATER: 'Repeater' }

export const UNIT_STATUS_LABEL: Record<HardwareUnitStatus, string> = { IN_STOCK: 'In stock', WITH_RESELLER: 'With reseller', ASSIGNED: 'Assigned' }
export const UNIT_STATUS_TONE: Record<HardwareUnitStatus, 'green' | 'amber' | 'gray'> = { IN_STOCK: 'green', WITH_RESELLER: 'amber', ASSIGNED: 'gray' }

export const ORDER_STATUS_LABEL: Record<HardwareOrderStatus, string> = {
  PENDING_PAYMENT: 'Awaiting payment',
  PAID: 'Paid',
  SHIPPED: 'Shipped',
  CANCELLED: 'Cancelled',
}
export const ORDER_STATUS_TONE: Record<HardwareOrderStatus, 'amber' | 'green' | 'gray' | 'red'> = {
  PENDING_PAYMENT: 'amber',
  PAID: 'green',
  SHIPPED: 'gray',
  CANCELLED: 'red',
}

export const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'] as const

/** The most units the API accepts in one bulk add. */
export const MAX_BULK_UNITS = 500

/** Splits pasted MACs / serials on newlines, commas, semicolons and any whitespace. Blank entries are dropped
 * and repeats (ignoring case) are collapsed to their first occurrence. */
export function parseHardwareIds(text: string): string[] {
  const seen = new Set<string>()
  const ids: string[] = []
  for (const raw of text.split(/[\s,;]+/)) {
    const id = normalizeHardwareId(raw)
    if (!id || seen.has(id)) continue
    seen.add(id)
    ids.push(id)
  }
  return ids
}

const MAC_PATTERN = /^[0-9A-F]{2}([:-]?[0-9A-F]{2}){5}$/

/** Upper-cases a MAC or serial, and regroups a MAC typed as bare hex or with dashes to AA:BB:CC:DD:EE:FF, the form
 * the gateways report. Only a string that is entirely a MAC is regrouped; any other serial is left as typed. Mirrors
 * the server's HardwareIds.Normalize. */
export function normalizeHardwareId(raw: string): string {
  const id = raw.trim().toUpperCase()
  if (!MAC_PATTERN.test(id)) return id
  return id.replace(/[:-]/g, '').match(/.{2}/g)!.join(':')
}

/** Dollars typed into a form → whole cents, or null when it isn't a non-negative amount with at most two decimals. */
export function dollarsToCents(input: string): number | null {
  const s = input.trim().replace(/^\$/, '')
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null
  return Math.round(Number(s) * 100)
}

/** Whole cents → AUD for display. */
export const dollarsFromCents = (cents: number) => formatAud(cents / 100)

export const centsToDollarsInput = (cents: number) => (cents / 100).toFixed(2)

/** "20 × Helmet Tag, 2 × LTE Gateway" */
export function orderItemsSummary(order: Pick<HardwareOrder, 'lines'>): string {
  return order.lines.map((l) => `${l.quantity} × ${l.name}`).join(', ')
}

export const orderUnitCount = (order: Pick<HardwareOrder, 'lines'>) => order.lines.reduce((n, l) => n + l.quantity, 0)

/** Who currently has a unit: the company it's installed at, else the reseller holding it, else nobody (stock). */
export function unitHolder(u: Pick<HardwareUnit, 'status' | 'companyName' | 'resellerName'>): string | null {
  if (u.status === 'ASSIGNED') return u.companyName
  if (u.status === 'WITH_RESELLER') return u.resellerName
  return null
}

/** Stock per product. Active catalogue products always get a row (so "0 in stock" is visible); an inactive product
 * only appears while units of it exist. */
export interface StockRow {
  productId: string
  productName: string
  inStock: number
  withResellers: number
  assigned: number
}
export function stockSummary(units: readonly HardwareUnit[], products: readonly HardwareProduct[] = []): StockRow[] {
  const rows = new Map<string, StockRow>()
  const blank = (productId: string, productName: string): StockRow => ({ productId, productName, inStock: 0, withResellers: 0, assigned: 0 })
  for (const p of products) if (p.active) rows.set(p.id, blank(p.id, p.name))
  for (const u of units) {
    const row = rows.get(u.productId) ?? blank(u.productId, u.productName)
    if (u.status === 'IN_STOCK') row.inStock++
    else if (u.status === 'WITH_RESELLER') row.withResellers++
    else row.assigned++
    rows.set(u.productId, row)
  }
  return [...rows.values()]
}

/** Oldest-first pick of `count` unit ids from a product's in-stock units. */
export function autoPickUnits(units: readonly HardwareUnit[], count: number): string[] {
  return [...units]
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.hardwareId.localeCompare(b.hardwareId))
    .slice(0, count)
    .map((u) => u.id)
}
